import { makeStyles } from '@material-ui/core/styles';

// components
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from 'components/shared/Typography';
import Input from 'components/shared/Input';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import LoadingButton from 'components/shared/LoadingButton';

// constants
import { SPACING_THIRD } from 'constants/spacing';
import { MOBILE_REGEX } from 'constants/regex';

// libs
import {
  getForgotTokenCookie,
  setForgotTokenCookie,
  removeForgotTokenCookie,
} from 'lib/Storage';
import { useEffect, useState } from 'react';

// hooks
import { useForgotPassword, useResetPassword } from 'hooks/auth';

// utils
import { digitsFaToEn } from 'persian-tools2';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  form: {
    width: '100%',
  },
  phoneInput: {
    '& .MuiInputBase-input::placeholder': {
      textAlign: 'center',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
  },
}));

/**
 * @component ForgetPassword
 */
function ForgetPassword() {
  const classes = useStyles();

  const { mutate: submitForgotRequest, isLoading: isLoadingForgotRequest } =
    useForgotPassword();
  const { mutate: submitResetRequest, isLoading: isLoadingResetRequest } =
    useResetPassword();

  useEffect(() => {
    let existingForgetToken = getForgotTokenCookie();
    if (existingForgetToken) setStage('reset');
  }, []);

  const [stage, setStage] = useState('forgot');

  const {
    handleSubmit: handelSubmitForgot,
    control: forgotController,
    formState: { errors: forgotErrors },
  } = useForm({
    defaultValues: {
      phoneNumber: '',
      username: '',
    },
  });

  const {
    handleSubmit: handelSubmitReset,
    control: resetController,
    formState: { errors: resetErrors },
    watch: resetWatch,
  } = useForm({
    defaultValues: {
      forgotOTP: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const validateRegex = (value) => {
    const innerValue = digitsFaToEn(value);
    if (!MOBILE_REGEX.test(innerValue)) {
      return 'شماره موبایل نامعتبر است';
    }
    return true;
  };

  const validatePassword = (value) => {
    const newPassword = resetWatch('newPassword');
    if (value !== newPassword) {
      return 'رمزهای عبور مطابقت ندارند';
    }
    return true;
  };

  const history = useHistory();

  const onForgotSubmit = (data) => {
    submitForgotRequest(data, {
      onSuccess: (response) => {
        if (response.status === 'OK') {
          let forgotToken = response.forgotToken;
          setForgotTokenCookie(forgotToken);
          setStage('reset');
        }
      },
    });
  };

  const onResetSubmit = (data) => {
    let forgotToken = getForgotTokenCookie();
    submitResetRequest(
      { ...data, forgotToken },
      {
        onSuccess: (response) => {
          if (response.status === 'OK') {
            removeForgotTokenCookie();
            history.push('/auth');
          }
        },
      }
    );
  };

  return (
    <Container maxWidth="sm">
      <div className={classes.root}>
        {stage === 'forgot' && (
          <form
            noValidate
            onSubmit={handelSubmitForgot(onForgotSubmit)}
            className={classes.form}
          >
            <Grid container spacing={SPACING_THIRD} justifyContent="center">
              <Grid item xs={12}>
                <Typography gutterBottom>
                  لطفا نام کاربری و شماره تلفن همراه خود را جهت بازیابی رمزعبور
                  خود وارد کنید
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="username"
                  rules={{ required: true }}
                  control={forgotController}
                  
                  render={({ field }) => (
                    <Input
                      label="نام کاربری"
                      className={classes.phoneInput}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText={
                        forgotErrors?.username?.type === 'required' && (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            نام کاربری مورد نیاز است
                          </Typography>
                        )
                      }
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="phoneNumber"
                  rules={{ required: true, validate: validateRegex }}
                  control={forgotController}
                  render={({ field }) => (
                    <Input
                      label="شماره تلفن"
                      type="tel"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      // placeholder="لطفا شماره موبایل خود را وارد کنید"
                      className={classes.phoneInput}
                      textAlign="center"
                      helperText={
                        forgotErrors?.phoneNumber &&
                        forgotErrors?.phoneNumber?.type === 'validate' ? (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                            locale="en"
                          >
                            {forgotErrors?.phoneNumber?.message}
                          </Typography>
                        ) : forgotErrors?.phoneNumber?.type === 'required' ? (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            وارد کردن این فیلد ضروری است
                          </Typography>
                        ) : null
                      }
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  type="submit"
                  size="small"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  ارسال کد
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}

        {stage === 'reset' && (
          <form
            noValidate
            onSubmit={handelSubmitReset(onResetSubmit)}
            className={classes.form}
          >
            <Grid container spacing={SPACING_THIRD} justifyContent="center">
              <Grid item xs={12}>
                <Typography gutterBottom>
                  لطفا کد ارسال شده به تلفن همراه خود را وارد کرده و رمز عبور
                  جدید خود را تنظیم کنید
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="forgotOTP"
                  rules={{ required: true }}
                  control={resetController}
                  render={({ field }) => (
                    <Input
                      label="کد بازیابی"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText={
                        resetErrors?.forgotOTP?.type === 'required' && (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            وارد کردن کد بازیابی ضروری است
                          </Typography>
                        )
                      }
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="newPassword"
                  rules={{ required: true, minLength: 6 }}
                  control={resetController}
                  render={({ field }) => (
                    <Input
                      label="رمز عبور جدید"
                      type="password"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText={
                        resetErrors?.newPassword?.type === 'required' ? (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            وارد کردن رمز عبور ضروری است
                          </Typography>
                        ) : resetErrors?.newPassword?.type === 'minLength' ? (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            رمز عبور باید حداقل ۶ کاراکتر باشد
                          </Typography>
                        ) : null
                      }
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="confirmPassword"
                  rules={{ required: true, validate: validatePassword }}
                  control={resetController}
                  render={({ field }) => (
                    <Input
                      label="تایید رمز عبور"
                      type="password"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText={
                        resetErrors?.confirmPassword?.type === 'required' ? (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            تایید رمز عبور ضروری است
                          </Typography>
                        ) : resetErrors?.confirmPassword?.type === 'validate' ? (
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            {resetErrors?.confirmPassword?.message}
                          </Typography>
                        ) : null
                      }
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  type="submit"
                  size="small"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  تنظیم رمز عبور
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      </div>
    </Container>
  );
}

export default ForgetPassword;
